/**
 * The icon set.
 *
 * Not a package. `lucide-react` is 1,600 components, and tree-shaking it
 * through a workspace package consumed from source is not something to bet a
 * mobile WebView's first paint on.
 *
 * Two rules do most of the structural work:
 *
 *   1. Directional marks and verbs are strokes; object nouns are fills. Half
 *      the set stops being a stroke at all, which is what kills the uniform
 *      weight a generated set has by construction.
 *   2. One `d` string is one shape with holes under `evenodd`; separate array
 *      entries are independent shapes that union. Nesting depth 1 is ink, 2 is
 *      a hole, 3 is ink again — which is how the same subpaths give a filled
 *      receipt with knocked-out bars and a hollow one with solid bars.
 *
 * ## Why v3 is drawn and not just measured
 *
 * v2 normalised the geometry — one cap height, one wall, one corner recipe —
 * and the tab bar still read as machine-made, because *perfect uniformity is
 * itself the tell*. A plain house, a plain bag, a plain receipt, a plain
 * bubble and a plain circle-person drawn to one recipe are the five shapes
 * every generated set produces. Precision was never the missing thing.
 *
 * So this pass keeps the optical discipline and adds the two things a hand is
 * responsible for: a specific idea per glyph, and deliberate irregularity.
 *
 *   - **Stress.** Vertical walls are 1.9 units, horizontals 1.6, diagonals
 *     1.72 — the stem/bar contrast of a typeface, not a plotter's single
 *     offset. It is why the ring of the person reads as an `O` and not as one
 *     circle subtracted from another, and why the bag's handle is heavier at
 *     the sides than over the crown.
 *   - **A noun, not a category.** The house has a door, so it is a house
 *     rather than a pentagon. The bag is a trapezoid with a crisp opening and
 *     a soft, weighted bottom, so it is a paper bag rather than a padlock. The
 *     receipt's torn edge has three *unequal* lobes at three different depths,
 *     so it reads as paper rather than as a loop that emitted four identical
 *     triangles. The bubble's tail grows out of the body's own outline and
 *     sweeps, so it is a balloon rather than a rectangle with a triangle
 *     stuck on.
 *   - **The solid is redrawn, not flooded.** Every internal mark exists twice:
 *     as ink inside the hollow glyph, and a little larger and further apart as
 *     a knockout inside the solid one, because at 26px a light shape on ink
 *     closes up where the same shape on the canvas does not. `RC_BAR1` vs
 *     `RC_BAR1F`, `BUB_DOTS` vs `BUB_DOTS_F`, `HEAD`/`TORSO` vs their `_F`.
 *
 * ## The optical system that survives from v2
 *
 *   - **Cap line.** A flat terminal sits on y=2.9 / y=21.1. A point or an arc
 *     overshoots it, because a circle drawn to the same height as a square
 *     reads short: the roof apex reaches 2.52, the bubble's tail tip and the
 *     receipt's deepest lobe 21.48, the person disc runs 2.6..21.4. Flattened
 *     for overshoot the five agree within 0.9%.
 *   - **Wall.** The gap between the outer contour and its counter *is* the
 *     perceived stroke of an unselected tab, so the stems are one number —
 *     1.9 units, ~2.06px at 26px — in all five.
 *   - **Corners.** One continuous-corner recipe: leave the edge at 1.25r with
 *     the handles at 0.35r. That is the Apple corner, it flows into the edge
 *     instead of sitting on it as an arc. `r` itself is chosen per corner, not
 *     globally — a bag's opening is crisp (1.3) and its loaded bottom is soft
 *     (3.3), and pretending those want the same number is what made v2 read as
 *     a template. Where two corners would each want more of an edge than it is
 *     long, both are scaled until they fit; without that the outline doubles
 *     back and the fill picks up a hairline spur along the torn edge.
 *
 * The cubics are generated from that system rather than typed, so the numbers
 * look unfriendly; they are not meant to be nudged by hand. Move a vertex, not
 * a control point.
 */

/* --- tab bar -----------------------------------------------------------------
 * Each has a filled and a hollow variant of the same silhouette at the same
 * optical size. Selection changes the interior, not just the colour, so it
 * survives being seen by someone who cannot tell the two colours apart.
 */

/* A house with a doorway, and the doorway belongs to the outer contour rather
 * than being a second shape laid on top. That is what lets both variants carry
 * it: the solid gets a door-shaped bite out of its bottom edge, and the hollow
 * one's band simply walks around the opening. A door in the silhouette is the
 * whole difference between a house and a rounded pentagon. */
const HOUSE_OUT =
  'M13.89 3.4 L19.92 8.6 C20.84 9.39 21.2 10.17 21.2 11.39 L21.2 17.6 C21.2 20.12 20.22 21.1 17.7 21.1 L14.23 21.1 C13.96 21.1 13.85 21 13.85 20.73 L13.85 13.46 C13.85 13.06 13.69 12.9 13.29 12.9 L10.71 12.9 C10.31 12.9 10.15 13.06 10.15 13.46 L10.15 20.73 C10.15 21 10.05 21.1 9.78 21.1 L6.3 21.1 C3.78 21.1 2.8 20.12 2.8 17.6 L2.8 11.39 C2.8 10.17 3.16 9.39 4.08 8.6 L10.11 3.4 C11.47 2.22 12.53 2.22 13.89 3.4 Z';
/* The counter is capped at r=1.15 everywhere. A true offset would open the
 * door's head to r≈2.2 and the opening would read as an archway. */
const HOUSE_IN =
  'M12.38 4.37 L18.92 10.01 C19.19 10.24 19.3 10.47 19.3 10.83 L19.3 18.19 C19.3 19.13 18.93 19.5 17.99 19.5 L16.25 19.5 C15.89 19.5 15.75 19.36 15.75 19 L15.75 12.74 C15.75 11.7 15.35 11.3 14.31 11.3 L9.69 11.3 C8.65 11.3 8.25 11.7 8.25 12.74 L8.25 19 C8.25 19.36 8.11 19.5 7.75 19.5 L6.01 19.5 C5.07 19.5 4.7 19.13 4.7 18.19 L4.7 10.83 C4.7 10.47 4.81 10.24 5.08 10.01 L11.62 4.37 C11.89 4.13 12.11 4.13 12.38 4.37 Z';

/* Wider at the mouth than at the base, and the two ends do not share a corner
 * radius: the opening is cut square (1.3) the way a paper bag's is, the bottom
 * is soft (3.3) the way one with something in it is. A rounded rectangle with
 * a semicircle on top is a padlock. */
const BAG_OUT =
  'M4.78 7.3 L19.23 7.3 C20.4 7.3 20.82 7.75 20.74 8.92 L20.22 16.98 C20.03 19.95 18.79 21.1 15.83 21.1 L8.17 21.1 C5.2 21.1 3.97 19.95 3.78 16.98 L3.26 8.92 C3.18 7.75 3.61 7.3 4.78 7.3 Z';
const BAG_IN =
  'M5.66 8.9 L18.34 8.9 C18.7 8.9 18.83 9.04 18.81 9.4 L18.28 17.57 C18.19 18.96 17.61 19.5 16.21 19.5 L7.79 19.5 C6.39 19.5 5.81 18.96 5.72 17.57 L5.19 9.4 C5.17 9.04 5.3 8.9 5.66 8.9 Z';
/* The handle carries the same stress as the rest of the set: its outer curve is
 * 4.7 × 3.38 and its inner one 2.8 × 1.83, so it is 1.9 thick at the springing
 * and 1.55 over the crown. A half-annulus of constant width — one circle inside
 * another — is the shape a script draws; this is the shape a hand does. Its
 * legs run 1.2 past the bag's mouth: at 26px a butt join there is a seam. */
const BAG_HANDLE =
  'M7.3 8.5 L7.3 5.9 A4.7 3.38 0 0 1 16.7 5.9 L16.7 8.5 L14.8 8.5 L14.8 5.9 A2.8 1.83 0 0 0 9.2 5.9 L9.2 8.5 Z';

/* Three lobes, no two alike: they sit at 5.5, 4.6 and 5.3 wide, bottom out at
 * 21.48, 21.42 and 21.30, and the notches between them cut to different heights
 * from different angles. Four identical triangles is a `for` loop; this is a
 * tear. Three rather than five because at 26px a half-lobe is under 3px and any
 * finer the most recognisable silhouette in the set turns to grey mush. */
const RC_OUT =
  'M7.3 2.9 L16.7 2.9 C18.86 2.9 19.7 3.74 19.7 5.9 L19.7 20.06 C19.7 20.7 19.46 21.02 18.84 21.2 L18.81 21.21 C17.65 21.55 16.78 21.46 15.72 20.91 L14.96 20.51 C14.52 20.29 14.2 20.31 13.8 20.61 L13.66 20.71 C12.57 21.52 11.72 21.52 10.63 20.74 L10.11 20.37 C9.71 20.08 9.39 20.07 8.97 20.34 L8.16 20.86 C7.14 21.51 6.3 21.64 5.14 21.3 L5.1 21.28 C4.53 21.11 4.3 20.81 4.3 20.21 L4.3 5.9 C4.3 3.74 5.14 2.9 7.3 2.9 Z';
/* A true offset of the tear, which is why the notches climb higher and open
 * wider than their outer twins while the lobes shrink: eroding ink dilates the
 * gaps in it. Faked by copying the outer tear upward, the band would pinch at
 * every notch. */
const RC_IN =
  'M7.01 4.5 L16.99 4.5 C17.57 4.5 17.8 4.73 17.8 5.31 L17.8 19.59 C17.8 19.75 17.74 19.83 17.59 19.88 L17.58 19.88 C17.43 19.92 17.31 19.91 17.17 19.84 L16.02 19.24 C14.71 18.56 13.74 18.64 12.55 19.51 L12.52 19.54 C12.24 19.74 12.02 19.75 11.74 19.54 L11.33 19.25 C10.08 18.36 9.1 18.33 7.81 19.16 L6.62 19.92 C6.53 19.98 6.46 19.99 6.35 19.96 L6.35 19.96 C6.24 19.93 6.2 19.88 6.2 19.77 L6.2 5.31 C6.2 4.73 6.43 4.5 7.01 4.5 Z';
/* Both bars are heavier where the pen lands than where it lifts — 1.68 falling
 * to 1.52, and 1.56 to 1.42. A capsule of constant width reads as a rule; a
 * line of print does not have constant width. */
const RC_BAR1 = 'M8.14 7.76 L15.94 7.84 A0.76 0.76 0 0 1 15.94 9.36 L8.14 9.44 A0.84 0.84 0 0 1 8.14 7.76 Z'; // prettier-ignore
const RC_BAR2 = 'M8.08 12.12 L12.69 12.19 A0.71 0.71 0 0 1 12.69 13.61 L8.08 13.68 A0.78 0.78 0 0 1 8.08 12.12 Z'; // prettier-ignore
/* The same two lines knocked out of ink instead of laid on the page: a shade
 * heavier and a shade further apart, because the solid closes over them
 * otherwise. This is the pair that stops the filled receipt from being the
 * hollow one flooded. */
const RC_BAR1F = 'M8.2 7.6 L15.88 7.68 A0.82 0.82 0 0 1 15.88 9.32 L8.2 9.4 A0.9 0.9 0 0 1 8.2 7.6 Z'; // prettier-ignore
const RC_BAR2F = 'M8.14 12.11 L12.63 12.18 A0.77 0.77 0 0 1 12.63 13.72 L8.14 13.79 A0.84 0.84 0 0 1 8.14 12.11 Z'; // prettier-ignore

/* The tail is part of the body's outline, not a shape stuck onto it, and the
 * corner it replaces is gone: the bottom edge runs left, drops away and sweeps
 * to a point at 21.48. That also lets the hollow variant close the tail to a
 * point on its own — an inward offset of a sharp corner retreats along the
 * bisector — instead of needing a second, hand-fudged inner tail. The four body
 * corners are 3.4 / 4.2 / 3.4 rather than one number: the corner furthest from
 * the tail is the roundest, which is how a balloon is drawn. */
const BUB_OUT =
  'M7.65 2.9 L15.35 2.9 C19.13 2.9 20.6 4.37 20.6 8.15 L20.6 13.25 C20.6 16.31 19.41 17.5 16.35 17.5 L12.3 17.5 C10.14 17.5 8.6 17.97 6.8 19.16 L3.52 21.34 C3.15 21.59 3.01 21.51 3.04 21.06 L3.21 18.72 C3.35 16.83 3.4 15.37 3.4 13.48 L3.4 7.15 C3.4 4.09 4.59 2.9 7.65 2.9 Z';
const BUB_IN =
  'M7.36 4.5 L15.64 4.5 C17.84 4.5 18.7 5.36 18.7 7.56 L18.7 13.84 C18.7 15.32 18.12 15.9 16.64 15.9 L12.25 15.9 C9.62 15.9 7.75 16.47 5.56 17.92 L5.36 18.05 C5.18 18.18 5.11 18.14 5.12 17.91 L5.13 17.87 C5.25 16.14 5.3 14.79 5.3 13.06 L5.3 6.56 C5.3 5.08 5.88 4.5 7.36 4.5 Z';
/* Wider than they are tall, 1.03 × 0.95, on the same horizontal stress as the
 * ring and the handle. Sat 0.1 right of the body's centre: the tail pulls the
 * glyph's mass left and the dots are the only thing that can pay it back. */
const BUB_DOTS =
  'M6.82 9.6 A1.03 0.95 0 0 1 8.88 9.6 A1.03 0.95 0 0 1 6.82 9.6 Z M11.07 9.6 A1.03 0.95 0 0 1 13.13 9.6 A1.03 0.95 0 0 1 11.07 9.6 Z M15.32 9.6 A1.03 0.95 0 0 1 17.38 9.6 A1.03 0.95 0 0 1 15.32 9.6 Z';
const BUB_DOTS_F =
  'M6.58 9.55 A1.12 1.03 0 0 1 8.82 9.55 A1.12 1.03 0 0 1 6.58 9.55 Z M10.98 9.55 A1.12 1.03 0 0 1 13.22 9.55 A1.12 1.03 0 0 1 10.98 9.55 Z M15.38 9.55 A1.12 1.03 0 0 1 17.62 9.55 A1.12 1.03 0 0 1 15.38 9.55 Z';

/* r=9.4, not the 9.8 that pure overshoot asks for, because a solid disc at the
 * same height as the bag out-weighs it. The whole set's discs are this one. */
const DISC_OUT = 'M2.6 12 A9.4 9.4 0 0 1 21.4 12 A9.4 9.4 0 0 1 2.6 12 Z';
/* The counter is an ellipse, not a circle: 7.5 across against 7.8 down leaves
 * the ring 1.9 thick at the sides and 1.6 at the crown. That is the stress of a
 * lowercase `o`, and it is the single clearest signal in the set that these
 * were drawn on a type grid rather than subtracted. */
const DISC_IN = 'M4.5 12 A7.5 7.8 0 0 1 19.5 12 A7.5 7.8 0 0 1 4.5 12 Z';
/* The head is taller than it is wide; the bust has a flat crown and steep
 * sides, so it reads as shoulders rather than as a hill. Its lower arc is 7.9 ×
 * 8.2 — deliberately *larger* than the counter, so it runs under the ring
 * instead of stopping dead on it. Two shapes that abut exactly leave an
 * antialiasing seam; two that overlap do not. */
const HEAD = 'M9.3 8.95 A2.7 2.9 0 0 1 14.7 8.95 A2.7 2.9 0 0 1 9.3 8.95 Z';
const TORSO =
  'M6.62 18 C6.62 15.43 9.22 13.85 12 13.85 C14.78 13.85 17.38 15.43 17.38 18 A7.9 8.2 0 0 1 6.62 18 Z';
/* Knocked out of the solid disc, so: a wider bust, a slightly larger head, and
 * a neck opened from 1.55 to 1.75. Same reasoning as the receipt's bars. */
const HEAD_F = 'M9.18 8.88 A2.82 3.02 0 0 1 14.82 8.88 A2.82 3.02 0 0 1 9.18 8.88 Z';
const TORSO_F =
  'M6.8 17.85 C6.8 15.49 9.55 14.05 12 14.05 C14.45 14.05 17.2 15.49 17.2 17.85 A7.68 7.95 0 0 1 6.8 17.85 Z';

/**
 * A tab glyph in both states.
 *
 * The vocabulary is a shop's, not a dashboard's: a bag rather than a 2×2 grid,
 * a receipt rather than a cardboard box — nothing here is shipped in a carton,
 * and a torn bottom edge is the most recognisable silhouette in the set.
 */
export const TAB_ICONS = {
  home: { fill: [HOUSE_OUT], light: [`${HOUSE_OUT} ${HOUSE_IN}`] },
  bag: { fill: [BAG_OUT, BAG_HANDLE], light: [`${BAG_OUT} ${BAG_IN}`, BAG_HANDLE] },
  receipt: {
    fill: [`${RC_OUT} ${RC_BAR1F} ${RC_BAR2F}`],
    light: [`${RC_OUT} ${RC_IN} ${RC_BAR1} ${RC_BAR2}`],
  },
  bubble: { fill: [`${BUB_OUT} ${BUB_DOTS_F}`], light: [`${BUB_OUT} ${BUB_IN} ${BUB_DOTS}`] },
  // The bust is its own element rather than a subpath: as part of the ring's
  // `evenodd` string it would have to stop exactly on the counter, and exactly
  // is where the seam lives.
  person: {
    fill: [`${DISC_OUT} ${HEAD_F} ${TORSO_F}`],
    light: [`${DISC_OUT} ${DISC_IN}`, HEAD, TORSO],
  },
} as const;

export type TabIconName = keyof typeof TAB_ICONS;

/* --- inline ------------------------------------------------------------------ */

/** Drawn with a stroke: directional marks and verbs. */
export const STROKE_ICONS = {
  chevron: ['M10.2 6.4 L14.6 12 L10.2 17.6'],
  search: ['M10.8 4.4 A6.4 6.4 0 0 1 10.8 17.2 A6.4 6.4 0 0 1 10.8 4.4 Z', 'M15.5 15.5 L20.3 20.3'],
  close: ['M6.4 6.4 L17.6 17.6', 'M17.6 6.4 L6.4 17.6'],
  check: ['M4.2 12.5 L9.3 17.6 L19.8 6.6'],
  // A 300° arc; its solid head lives in FILL_ICONS and the two are drawn
  // together. Faking the head with stroked corner segments is one of the
  // loudest generic tells there is.
  refreshArc: ['M12 4.4 A7.6 7.6 0 1 0 18.58 8.2'],
  spinner: ['M12 3.4 A8.6 8.6 0 1 1 5.9 5.9'],
} as const;

/**
 * Drawn as a fill: object nouns.
 *
 * Same optical system as the tab bar. Every disc in here is DISC_OUT's radius,
 * and the landscape family — card, banknote, wallet, ticket, key — shares one
 * 19.6-unit width and is centred on y=12, because two of them used to run to
 * x=22.2 and sat visibly wider than everything they appeared beside in a row.
 */
export const FILL_ICONS = {
  refreshHead: ['M12 2.1 L15.3 4.5 L12 6.9 Z'],
  // The tab silhouettes, available inline too — an empty orders screen should
  // show the same receipt the tab does, not a different noun for the same idea.
  // These are the solid variants, so they carry the solid variants' internals.
  bag: [BAG_OUT, BAG_HANDLE],
  receipt: [`${RC_OUT} ${RC_BAR1F} ${RC_BAR2F}`],
  bubble: [`${BUB_OUT} ${BUB_DOTS_F}`],
  person: [`${DISC_OUT} ${HEAD_F} ${TORSO_F}`],
  wallet: [
    'M5.6 5.3 L18.4 5.3 Q21.8 5.3 21.8 8.7 L21.8 15.3 Q21.8 18.7 18.4 18.7 L5.6 18.7 Q2.2 18.7 2.2 15.3 L2.2 8.7 Q2.2 5.3 5.6 5.3 Z M14.2 10.2 L18.2 10.2 A1.8 1.8 0 0 1 18.2 13.8 L14.2 13.8 A1.8 1.8 0 0 1 14.2 10.2 Z M18.1 10.85 A1.15 1.15 0 0 1 18.1 13.15 A1.15 1.15 0 0 1 18.1 10.85 Z',
  ],
  ticket: [
    'M5 5.6 L19 5.6 Q21.8 5.6 21.8 8.4 L21.8 10 A2 2 0 0 0 21.8 14 L21.8 15.6 Q21.8 18.4 19 18.4 L5 18.4 Q2.2 18.4 2.2 15.6 L2.2 14 A2 2 0 0 0 2.2 10 L2.2 8.4 Q2.2 5.6 5 5.6 Z M11.1 8.6 A0.9 0.9 0 0 1 12.9 8.6 L12.9 10.2 A0.9 0.9 0 0 1 11.1 10.2 Z M11.1 13.8 A0.9 0.9 0 0 1 12.9 13.8 L12.9 15.4 A0.9 0.9 0 0 1 11.1 15.4 Z',
  ],
  key: [
    'M7 7 A4.8 4.8 0 0 1 7 16.6 A4.8 4.8 0 0 1 7 7 Z M7 9.8 A2 2 0 0 1 7 13.8 A2 2 0 0 1 7 9.8 Z',
    'M10.2 10.4 L20.4 10.4 Q21.8 10.4 21.8 11.8 Q21.8 13.2 20.4 13.2 L20.4 15.2 Q20.4 16 19.6 16 Q18.8 16 18.8 15.2 L18.8 13.2 L16.4 13.2 L16.4 16.2 Q16.4 17 15.6 17 Q14.8 17 14.8 16.2 L14.8 13.2 L10.2 13.2 Z',
  ],
  inbox: [
    'M6 4.6 L18 4.6 Q21.6 4.6 21.6 8.2 L21.6 15.8 Q21.6 19.4 18 19.4 L6 19.4 Q2.4 19.4 2.4 15.8 L2.4 8.2 Q2.4 4.6 6 4.6 Z M7.2 7.2 L16.8 7.2 Q18.4 7.2 18.4 8.8 L18.4 12.9 L15.5 12.9 L14.4 14.9 L9.6 14.9 L8.5 12.9 L5.6 12.9 L5.6 8.8 Q5.6 7.2 7.2 7.2 Z',
  ],
  copy: [
    'M5.67 2.6 L11.42 2.6 Q14.11 2.6 14.11 5.29 L14.11 6.72 L11.81 6.72 Q8.07 6.72 8.07 10.47 L8.07 16.22 L5.67 16.22 Q2.98 16.22 2.98 13.53 L2.98 5.29 Q2.98 2.6 5.67 2.6 Z',
    'M11.81 7.78 L18.33 7.78 Q21.02 7.78 21.02 10.47 L21.02 18.71 Q21.02 21.4 18.33 21.4 L11.81 21.4 Q9.12 21.4 9.12 18.71 L9.12 10.47 Q9.12 7.78 11.81 7.78 Z',
  ],
  // Used where a wallet would be ambiguous at chip size.
  banknote: [
    'M4.7 6.24 L19.3 6.24 Q21.8 6.24 21.8 8.73 L21.8 15.27 Q21.8 17.76 19.3 17.76 L4.7 17.76 Q2.2 17.76 2.2 15.27 L2.2 8.73 Q2.2 6.24 4.7 6.24 Z M12 9.41 A2.59 2.59 0 0 1 12 14.59 A2.59 2.59 0 0 1 12 9.41 Z M5.47 10.99 A1.01 1.01 0 0 1 5.47 13.01 A1.01 1.01 0 0 1 5.47 10.99 Z M18.53 10.99 A1.01 1.01 0 0 1 18.53 13.01 A1.01 1.01 0 0 1 18.53 10.99 Z',
  ],
  circleCheck: [
    'M12 2.6 A9.4 9.4 0 0 1 12 21.4 A9.4 9.4 0 0 1 12 2.6 Z M10.8 16.22 L17.37 9.65 A1.06 1.06 0 0 0 15.88 8.16 L10.8 13.25 L8.12 10.56 A1.06 1.06 0 0 0 6.63 12.05 Z',
  ],
  clock: [
    'M12 2.6 A9.4 9.4 0 0 1 12 21.4 A9.4 9.4 0 0 1 12 2.6 Z M11.14 6.82 A0.86 0.86 0 0 1 12.86 6.82 L12.86 11.62 L16.41 13.68 A0.86 0.86 0 0 1 15.55 15.17 L11.14 12.62 Z',
  ],
  alert: [
    'M12 2.6 A9.4 9.4 0 0 1 12 21.4 A9.4 9.4 0 0 1 12 2.6 Z M11.09 6.82 A0.91 0.91 0 0 1 12.91 6.82 L12.91 13.06 A0.91 0.91 0 0 1 11.09 13.06 Z M12 15.36 A1.1 1.1 0 0 1 12 17.56 A1.1 1.1 0 0 1 12 15.36 Z',
  ],
  card: [
    'M4.7 5.27 L19.3 5.27 Q21.8 5.27 21.8 7.77 L21.8 16.23 Q21.8 18.73 19.3 18.73 L4.7 18.73 Q2.2 18.73 2.2 16.23 L2.2 7.77 Q2.2 5.27 4.7 5.27 Z M2.2 8.93 L21.8 8.93 L21.8 11.42 L2.2 11.42 Z',
  ],
  // Points overshoot the cap line, same as the roof apex does.
  star: [
    'M12 2.6 L14.91 9.02 L21.76 9.82 L16.67 14.54 L18.06 21.4 L12 17.95 L5.94 21.4 L7.33 14.54 L2.24 9.82 L9.09 9.02 Z',
  ],
} as const;

export type StrokeIconName = keyof typeof STROKE_ICONS;
export type FillIconName = keyof typeof FILL_ICONS;
export type IconName = StrokeIconName | FillIconName;

/** Every glyph, for the renderer to look up by name. */
export const ICONS: Record<IconName, readonly string[]> = { ...STROKE_ICONS, ...FILL_ICONS };
